import { useState, useEffect, useMemo } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { API_URL } from "../../config/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Train,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Settings,
  Key,
  Calendar,
  Loader2,
  MapPin,
  ArrowRight,
  Eye,
  ChevronRight,
  Package,
  Filter,
  Trash2,
} from "lucide-react";


export default function MetransTrackingContent() {
  const { token } = useAuth();
  const [containerNumber, setContainerNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [credentialsConfigured, setCredentialsConfigured] = useState(false);
  const [showCredentialsForm, setShowCredentialsForm] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [trackingJobs, setTrackingJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [manualOtp, setManualOtp] = useState("");
  const [scheduledStatus, setScheduledStatus] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    checkCredentials();
    fetchRecentJobs();
    fetchScheduledStatus();
  }, []);

  // Filter jobs based on active filter
  const filteredJobs = useMemo(() => {
    if (activeFilter === "all") return trackingJobs;
    if (activeFilter === "pending") return trackingJobs.filter(j => j.status === "pending" || j.status === "scheduled" || j.status === "scheduled_pending");
    if (activeFilter === "completed") return trackingJobs.filter(j => j.status === "completed");
    if (activeFilter === "failed") return trackingJobs.filter(j => j.status === "failed");
    if (activeFilter === "otp") return trackingJobs.filter(j => j.status === "awaiting_otp" || j.needs_otp);
    return trackingJobs;
  }, [trackingJobs, activeFilter]);

  // Job counts for filter badges
  const jobCounts = useMemo(() => ({
    all: trackingJobs.length,
    pending: trackingJobs.filter(j => j.status === "pending" || j.status === "scheduled" || j.status === "scheduled_pending").length,
    completed: trackingJobs.filter(j => j.status === "completed").length,
    failed: trackingJobs.filter(j => j.status === "failed").length,
    otp: trackingJobs.filter(j => j.status === "awaiting_otp" || j.needs_otp).length,
  }), [trackingJobs]);

  const handleRefreshJobs = async () => {
    setIsRefreshing(true);
    try {
      await fetchRecentJobs();
      toast.success("Tracking jobs refreshed");
    } catch (error) {
      toast.error("Failed to refresh jobs");
    } finally {
      setIsRefreshing(false);
    }
  };

  const checkCredentials = async () => {
    try {
      const response = await axios.get(`${API_URL}/metrans-tracking/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredentialsConfigured(response.data.configured);
      if (response.data.username) {
        setUsername(response.data.username);
      }
    } catch (error) {
      console.error("Error checking credentials:", error);
    }
  };

  const saveCredentials = async () => {
    if (!username || !password) {
      toast.error("Please enter both username and password");
      return;
    }

    try {
      await axios.post(`${API_URL}/metrans-tracking/credentials`, {
        username,
        password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Credentials saved successfully");
      setCredentialsConfigured(true);
      setShowCredentialsForm(false);
      setPassword("");
    } catch (error) {
      toast.error("Failed to save credentials");
    }
  };

  const fetchRecentJobs = async () => {
    try {
      const response = await axios.get(`${API_URL}/metrans-tracking/jobs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrackingJobs(response.data.jobs || []);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  const fetchScheduledStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/metrans-tracking/scheduled/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScheduledStatus(response.data);
    } catch (error) {
      console.error("Error fetching scheduled status:", error);
    }
  };

  const viewJobDetails = async (jobId) => {
    try {
      setLoadingDetail(true);
      const response = await axios.get(`${API_URL}/metrans-tracking/track/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedJob(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error("Failed to load tracking details");
      console.error("Error fetching job details:", error);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Helper to determine if a schedule leg is in the future
  const isFutureSchedule = (leg) => {
    if (!leg.departure && !leg.arrival) return false;
    const dateStr = leg.departure || leg.arrival;
    if (!dateStr) return false;
    try {
      const legDate = new Date(dateStr);
      return legDate > new Date();
    } catch {
      return false;
    }
  };

  // Helper to get current/last state from schedule
  const getCurrentState = (result) => {
    if (!result) return null;
    if (result.current_state) return result.current_state;
    if (result.container_state?.state) return result.container_state.state;
    return null;
  };

  // Get upcoming schedule (legs that haven't completed yet)
  const getUpcomingSchedule = (result) => {
    if (!result?.transport_schedule) return [];
    return result.transport_schedule.filter(leg => 
      !leg.state?.toLowerCase().includes("arrived") && 
      !leg.state?.toLowerCase().includes("completed")
    );
  };

  const startTracking = async () => {
    if (!containerNumber.trim()) {
      toast.error("Please enter a container number");
      return;
    }

    if (!credentialsConfigured) {
      toast.error("Please configure Metrans credentials first");
      setShowCredentialsForm(true);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/metrans-tracking/track`, {
        container_number: containerNumber.trim().toUpperCase()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Tracking job started");
      setActiveJob(response.data);
      setTrackingJobs(prev => [response.data, ...prev]);

      // Start polling for status
      pollJobStatus(response.data.job_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start tracking");
    } finally {
      setLoading(false);
    }
  };

  const pollJobStatus = async (jobId) => {
    const maxAttempts = 180;
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        toast.error("Tracking timeout - please check status manually");
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/metrans-tracking/track/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setActiveJob(response.data);
        setTrackingJobs(prev => prev.map(j => 
          j.job_id === jobId ? response.data : j
        ));

        if (response.data.status === "completed") {
          toast.success("Tracking completed successfully!");
          return;
        } else if (response.data.status === "failed") {
          toast.error(response.data.error || "Tracking failed");
          return;
        } else if (response.data.status === "awaiting_otp") {
          attempts++;
          setTimeout(poll, 3000);
          return;
        }

        attempts++;
        setTimeout(poll, 3000);
      } catch (error) {
        console.error("Error polling status:", error);
        attempts++;
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const submitManualOtp = async () => {
    if (!activeJob || !manualOtp.trim() || manualOtp.length < 4) {
      toast.error("Please enter a valid OTP code");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/metrans-tracking/otp/${activeJob.job_id}`, null, {
        params: { otp: manualOtp.trim() },
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("OTP submitted! Tracking will continue automatically...");
      setManualOtp("");
      pollJobStatus(activeJob.job_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit OTP");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "failed":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "awaiting_otp":
        return <AlertCircle className="h-5 w-5 text-amber-500" />;
      case "in_progress":
      case "pending":
      default:
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-6 pb-96">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl p-6 text-white mb-8">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <Train className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Metrans Rail Tracking</h1>
            <p className="text-white/80">Track rail containers via Metrans portal</p>
          </div>
        </div>
      </div>

      {/* Credentials Status */}
      <div className="flex items-center gap-3 mb-6">
        {credentialsConfigured ? (
          <>
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm text-slate-600">
              Credentials configured for: <strong>{username}</strong>
            </span>
          </>
        ) : (
          <>
            <AlertCircle className="h-5 w-5 text-amber-500" />
            <span className="text-sm text-amber-600">Credentials not configured</span>
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowCredentialsForm(!showCredentialsForm)}
        >
          <Settings className="h-4 w-4" />
        </Button>
      </div>

      {/* Credentials Form */}
      {showCredentialsForm && (
        <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Key className="h-5 w-5 text-orange-500" />
            Metrans Portal Credentials
          </h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Username</label>
              <Input
                data-testid="metrans-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="your.email@company.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
              <Input
                data-testid="metrans-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={saveCredentials} className="bg-orange-500 hover:bg-orange-600">
              Save Credentials
            </Button>
            <Button variant="outline" onClick={() => setShowCredentialsForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Track Container */}
      <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
        <h3 className="font-semibold mb-4">Track Container</h3>
        <div className="flex gap-3">
          <Input
            data-testid="container-number-input"
            value={containerNumber}
            onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
            placeholder="Enter container number (e.g., DFSU 204941-0)"
            className="max-w-md"
            onKeyDown={(e) => e.key === "Enter" && startTracking()}
          />
          <Button
            data-testid="track-btn"
            onClick={startTracking}
            disabled={loading || !credentialsConfigured}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Track
          </Button>
        </div>
      </div>

      {/* Active Job Status */}
      {activeJob && (
        <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {getStatusIcon(activeJob.status)}
              <h3 className="text-lg font-semibold">
                Tracking: {activeJob.container_number}
              </h3>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              activeJob.status === "completed" ? "bg-green-100 text-green-700" :
              activeJob.status === "failed" ? "bg-red-100 text-red-700" :
              activeJob.status === "awaiting_otp" ? "bg-amber-100 text-amber-700" :
              "bg-blue-100 text-blue-700"
            }`}>
              {activeJob.status === "awaiting_otp" ? "Waiting for OTP" : activeJob.status}
            </span>
          </div>

          {/* Steps */}
          {activeJob.steps && activeJob.steps.length > 0 && (
            <div className="space-y-2 mb-4">
              {activeJob.steps.map((step, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  {step.status === "success" ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : step.status === "failed" ? (
                    <XCircle className="h-4 w-4 text-red-500" />
                  ) : step.status === "awaiting_otp" ? (
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                  ) : (
                    <Clock className="h-4 w-4 text-blue-500" />
                  )}
                  <span className="text-slate-600">{step.step}</span>
                </div>
              ))}
            </div>
          )}

          {/* Manual OTP Input */}
          {(activeJob.status === "awaiting_otp" || activeJob.needs_otp) && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-5 mt-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-6 w-6 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold text-amber-800 text-lg">OTP Code Required</p>
                  <p className="text-sm text-amber-700 mb-4">
                    Metrans has sent a one-time password (OTP) to your registered email. 
                    Please check your email and enter the code below:
                  </p>
                  <div className="flex gap-3 items-center">
                    <Input
                      data-testid="otp-input"
                      value={manualOtp}
                      onChange={(e) => setManualOtp(e.target.value.replace(/\D/g, ""))}
                      placeholder="Enter OTP code"
                      className="max-w-[180px] text-lg font-mono tracking-widest text-center"
                      maxLength={8}
                      onKeyDown={(e) => e.key === "Enter" && submitManualOtp()}
                    />
                    <Button 
                      data-testid="submit-otp-btn"
                      onClick={submitManualOtp} 
                      disabled={!manualOtp.trim() || manualOtp.length < 4}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Submit OTP
                    </Button>
                  </div>
                  <p className="text-xs text-amber-600 mt-3">
                    You have approximately 5 minutes to enter the code before the session expires.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {activeJob.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Tracking Failed</p>
                  <p className="text-sm text-red-600">{activeJob.error}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scheduled Tracking Status */}
      {scheduledStatus && (
        <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-orange-500" />
              <h3 className="text-lg font-semibold">Scheduled Tracking</h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchScheduledStatus}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-orange-50 rounded-lg p-4 text-center border border-orange-200">
              <p className="text-2xl font-bold text-orange-600">{scheduledStatus.enabled_pfs}</p>
              <p className="text-sm text-slate-600">PFs Enabled</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
              <p className="text-2xl font-bold text-blue-600">{scheduledStatus.pending_jobs}</p>
              <p className="text-sm text-slate-600">Pending Jobs</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
              <p className="text-2xl font-bold text-green-600">
                {trackingJobs.filter(j => j.status === 'completed').length}
              </p>
              <p className="text-sm text-slate-600">Completed</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center border border-red-200">
              <p className="text-2xl font-bold text-red-600">
                {trackingJobs.filter(j => j.status === 'failed').length}
              </p>
              <p className="text-sm text-slate-600">Failed</p>
            </div>
          </div>
          
          {/* Schedule Info */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg mb-4">
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Scheduled: <strong>Twice daily (6:00 and 15:00 CET)</strong></span>
            </div>
            {scheduledStatus.last_run && (
              <span className="text-xs text-slate-500">
                Last run: {new Date(scheduledStatus.last_run.run_at).toLocaleString()} 
                ({scheduledStatus.last_run.jobs_created} created, {scheduledStatus.last_run.skipped} skipped)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Containers in Tracking Queue - IMPROVED UI */}
      <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Package className="h-5 w-5 text-orange-500" />
            Containers in Metrans Tracking
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshJobs}
            disabled={isRefreshing}
            className="border-orange-300 text-orange-600 hover:bg-orange-50"
            data-testid="refresh-jobs-btn"
          >
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Summary Stats Cards */}
        <div className="grid grid-cols-5 gap-3 mb-6">
          <div 
            onClick={() => setActiveFilter("all")}
            className={`rounded-lg p-4 text-center cursor-pointer transition-all border-2 ${
              activeFilter === "all" 
                ? "border-slate-600 bg-slate-100" 
                : "border-transparent bg-slate-50 hover:border-slate-300"
            }`}
            data-testid="filter-all"
          >
            <p className="text-2xl font-bold text-slate-700">{jobCounts.all}</p>
            <p className="text-xs text-slate-500 font-medium">Total</p>
          </div>
          <div 
            onClick={() => setActiveFilter("pending")}
            className={`rounded-lg p-4 text-center cursor-pointer transition-all border-2 ${
              activeFilter === "pending" 
                ? "border-blue-500 bg-blue-100" 
                : "border-transparent bg-blue-50 hover:border-blue-300"
            }`}
            data-testid="filter-pending"
          >
            <p className="text-2xl font-bold text-blue-600">{jobCounts.pending}</p>
            <p className="text-xs text-blue-600 font-medium">Pending</p>
          </div>
          <div 
            onClick={() => setActiveFilter("completed")}
            className={`rounded-lg p-4 text-center cursor-pointer transition-all border-2 ${
              activeFilter === "completed" 
                ? "border-green-500 bg-green-100" 
                : "border-transparent bg-green-50 hover:border-green-300"
            }`}
            data-testid="filter-completed"
          >
            <p className="text-2xl font-bold text-green-600">{jobCounts.completed}</p>
            <p className="text-xs text-green-600 font-medium">Completed</p>
          </div>
          <div 
            onClick={() => setActiveFilter("failed")}
            className={`rounded-lg p-4 text-center cursor-pointer transition-all border-2 ${
              activeFilter === "failed" 
                ? "border-red-500 bg-red-100" 
                : "border-transparent bg-red-50 hover:border-red-300"
            }`}
            data-testid="filter-failed"
          >
            <p className="text-2xl font-bold text-red-600">{jobCounts.failed}</p>
            <p className="text-xs text-red-600 font-medium">Failed</p>
          </div>
          <div 
            onClick={() => setActiveFilter("otp")}
            className={`rounded-lg p-4 text-center cursor-pointer transition-all border-2 ${
              activeFilter === "otp" 
                ? "border-amber-500 bg-amber-100" 
                : "border-transparent bg-amber-50 hover:border-amber-300"
            }`}
            data-testid="filter-otp"
          >
            <p className="text-2xl font-bold text-amber-600">{jobCounts.otp}</p>
            <p className="text-xs text-amber-600 font-medium">Needs OTP</p>
          </div>
        </div>

        {/* Active Filter Indicator */}
        {activeFilter !== "all" && (
          <div className="flex items-center justify-between mb-4 p-2 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Filter className="h-4 w-4" />
              <span>Showing <strong>{filteredJobs.length}</strong> {activeFilter} containers</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setActiveFilter("all")}
              className="text-slate-500 hover:text-slate-700"
            >
              Clear filter
            </Button>
          </div>
        )}
        
        {trackingJobs.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Train className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>No containers in tracking queue</p>
            <p className="text-xs mt-1">Add containers to PFs with Metrans tracking enabled</p>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <Filter className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>No containers match this filter</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setActiveFilter("all")}
              className="mt-2"
            >
              Show all containers
            </Button>
          </div>
        ) : (
          <>
            {/* Container List */}
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Container</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Position Folder</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Destination</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Last Update</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Location</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.map((job, index) => (
                    <tr 
                      key={job.job_id || `${job.container_number}-${index}`} 
                      className="border-t border-slate-100 hover:bg-orange-50/30 transition-colors"
                      data-testid={`tracking-row-${job.container_number}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            job.status === "completed" ? "bg-green-500" :
                            job.status === "failed" ? "bg-red-500" :
                            job.status === "awaiting_otp" ? "bg-amber-500 animate-pulse" :
                            "bg-blue-500"
                          }`} />
                          <span className="font-mono font-semibold text-slate-900">{job.container_number}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {job.pf_number ? (
                          <span className="px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-md text-xs font-semibold">
                            {job.pf_number}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs italic">Not linked</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {job.destination ? (
                          <div className="flex items-center gap-1 text-slate-700">
                            <MapPin className="h-3 w-3 text-orange-400" />
                            {job.destination}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          job.status === "completed" ? "bg-green-100 text-green-700" :
                          job.status === "failed" ? "bg-red-100 text-red-700" :
                          job.status === "awaiting_otp" ? "bg-amber-100 text-amber-700" :
                          job.status === "in_progress" ? "bg-blue-100 text-blue-700" :
                          job.status === "scheduled_pending" ? "bg-purple-100 text-purple-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>
                          {getStatusIcon(job.status)}
                          {job.status === "scheduled_pending" ? "Scheduled" : job.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {job.updated_at || job.created_at ? (
                          <span title={new Date(job.updated_at || job.created_at).toLocaleString()}>
                            {new Date(job.updated_at || job.created_at).toLocaleString('en-GB', {
                              day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {job.tracking_data?.current_location ? (
                          <div className="flex items-center gap-1 text-slate-600">
                            <MapPin className="h-3 w-3 text-green-500" />
                            {job.tracking_data.current_location}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {job.status === "completed" && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => viewJobDetails(job.job_id)}
                              className="text-slate-600 hover:text-orange-600"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          {(job.status === "pending" || job.status === "failed" || job.status === "scheduled_pending") && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setContainerNumber(job.container_number);
                                setTimeout(() => startTracking(), 100);
                              }}
                              className="text-slate-600 hover:text-orange-600"
                              title="Re-track"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Results count */}
            <div className="mt-3 text-xs text-slate-500 text-right">
              Showing {filteredJobs.length} of {trackingJobs.length} containers
            </div>
          </>
        )}
      </div>

      {/* Recent Jobs */}
      {trackingJobs.length > 0 && false && (
        <div className="bg-white rounded-xl border shadow-sm p-6 mb-8">
          <h3 className="text-lg font-semibold mb-4">Recent Tracking Jobs</h3>
          <div className="space-y-2">
            {trackingJobs.slice(0, 10).map((job) => (
              <div 
                key={job.job_id} 
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                onClick={() => job.status === "completed" ? viewJobDetails(job.job_id) : null}
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(job.status)}
                  <div>
                    <p className="font-medium">{job.container_number}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(job.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    job.status === "completed" ? "bg-green-100 text-green-700" :
                    job.status === "failed" ? "bg-red-100 text-red-700" :
                    job.status === "awaiting_otp" ? "bg-amber-100 text-amber-700" :
                    "bg-blue-100 text-blue-700"
                  }`}>
                    {job.status}
                  </span>
                  {job.status === "completed" && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewJobDetails(job.job_id);
                      }}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h4 className="font-semibold text-blue-800 mb-2">How it works</h4>
        <ol className="list-decimal list-inside space-y-2 text-sm text-blue-700">
          <li>Configure your Metrans portal credentials (stored securely)</li>
          <li>Enter a container number to track</li>
          <li>System logs into Metrans portal automatically</li>
          <li><strong>When prompted, enter the OTP code</strong> from your Metrans email</li>
          <li>System completes login and fetches container status</li>
          <li>Position Folders are updated automatically if linked</li>
        </ol>
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> Metrans uses 2-factor authentication. When tracking starts, 
            you will receive an OTP code to your registered Metrans email. Enter this code 
            when prompted to continue the tracking process.
          </p>
        </div>
      </div>

      {/* Tracking Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Train className="h-5 w-5 text-orange-500" />
              Tracking Details: {selectedJob?.container_number}
            </DialogTitle>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            </div>
          ) : selectedJob ? (
            <div className="space-y-6">
              {/* Current State */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-5 w-5 text-orange-600" />
                  <h4 className="font-semibold text-orange-800">Current State</h4>
                </div>
                <p className="text-sm text-orange-900">
                  {getCurrentState(selectedJob.result) || "No state information available"}
                </p>
                {selectedJob.result?.container_state && (
                  <div className="mt-2 text-xs text-orange-700">
                    <span className="font-medium">Container:</span> {selectedJob.result.container_state.container_number || selectedJob.container_number}
                    {selectedJob.result.container_state.container_type && (
                      <span className="ml-3"><span className="font-medium">Type:</span> {selectedJob.result.container_state.container_type}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Upcoming Schedule */}
              {selectedJob.result?.transport_schedule?.length > 0 && (
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <h4 className="font-semibold">Transport Schedule</h4>
                  </div>
                  <div className="space-y-3">
                    {selectedJob.result.transport_schedule.map((leg, index) => {
                      const isCompleted = leg.state?.toLowerCase().includes("arrived") || 
                                         leg.state?.toLowerCase().includes("completed") ||
                                         leg.state?.toLowerCase().includes("loaded");
                      const isCurrent = leg.state?.toLowerCase().includes("transit") ||
                                       leg.state?.toLowerCase().includes("en route") ||
                                       leg.state?.toLowerCase().includes("unload");
                      
                      return (
                        <div 
                          key={index} 
                          className={`flex items-center gap-3 p-3 rounded-lg ${
                            isCompleted ? "bg-green-50 border border-green-200" :
                            isCurrent ? "bg-amber-50 border border-amber-300" :
                            "bg-slate-50 border border-slate-200"
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            isCompleted ? "bg-green-500 text-white" :
                            isCurrent ? "bg-amber-500 text-white" :
                            "bg-slate-300 text-slate-700"
                          }`}>
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 text-sm font-medium">
                              <span className="truncate">{leg.from_location || "—"}</span>
                              <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                              <span className="truncate">{leg.to_location || "—"}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-600">
                              {leg.departure && (
                                <span><span className="font-medium">Dep:</span> {leg.departure}</span>
                              )}
                              {leg.arrival && (
                                <span><span className="font-medium">Arr:</span> {leg.arrival}</span>
                              )}
                              {leg.train && (
                                <span><span className="font-medium">Train:</span> {leg.train}</span>
                              )}
                            </div>
                          </div>
                          {leg.state && (
                            <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                              isCompleted ? "bg-green-200 text-green-800" :
                              isCurrent ? "bg-amber-200 text-amber-800" :
                              "bg-slate-200 text-slate-700"
                            }`}>
                              {leg.state}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Container History */}
              {selectedJob.result?.container_history?.length > 0 && (
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Clock className="h-5 w-5 text-slate-600" />
                    <h4 className="font-semibold">Container History</h4>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedJob.result.container_history.map((entry, index) => (
                      <div key={index} className="text-sm text-slate-600 py-1 border-b border-slate-100 last:border-0">
                        {entry.data?.join(" | ") || JSON.stringify(entry)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="text-xs text-slate-500 pt-2 border-t">
                <div className="flex flex-wrap gap-x-4">
                  <span><span className="font-medium">Job ID:</span> {selectedJob.job_id}</span>
                  <span><span className="font-medium">Fetched:</span> {selectedJob.result?.fetched_at ? new Date(selectedJob.result.fetched_at).toLocaleString() : "N/A"}</span>
                  <span><span className="font-medium">Status:</span> {selectedJob.status}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              No tracking data available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
